import { ComponentType, lazy, useEffect, useState } from "react";

// True while scripts/prerender.mjs is snapshotting the page at build time.
// In this mode, live numbers are left as skeletons (so they never get frozen into the saved HTML)
// and heavy media such as the banner video is not started.
export const isPrerender = (): boolean =>
  typeof window !== "undefined" &&
  (window as typeof window & { __PRERENDER__?: boolean }).__PRERENDER__ === true;

// Viewport width the snapshot is rendered at (keep in sync with VIEWPORT in scripts/prerender.mjs)
export const PRERENDER_VIEWPORT_WIDTH = 390;

// True from the moment index.js decides to hydrate a prerendered page until the route content inside the
// App Suspense boundary has committed (see PrerenderBoundaryMark). While it is true, components must render
// exactly what the prerender snapshot contains.
let hydratingFromPrerender = false;

export const startHydratingFromPrerender = () => {
  hydratingFromPrerender = true;
};

export const finishHydratingFromPrerender = () => {
  hydratingFromPrerender = false;
};

export const isHydratingFromPrerender = (): boolean => hydratingFromPrerender;

/**
 * Returns `prerenderValue` on the hydration render of a prerendered page and `value` afterwards.
 * Use it for anything the snapshot cannot know (login state, localStorage, ...): the first render then
 * matches the saved HTML, and the real value appears right after mount. Outside hydration it is just `value`.
 */
export function useAfterHydration<T>(value: T, prerenderValue: T): T {
  const [hydrated, setHydrated] = useState(() => !hydratingFromPrerender);
  useEffect(() => {
    if (!hydrated) setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return hydrated ? value : prerenderValue;
}

/**
 * React.lazy that can be loaded ahead of rendering. A prerendered route whose page is lazy must be loaded
 * before hydrateRoot starts (index.js): if it suspends instead, any state update above it while its code is
 * still downloading (login state, ...) makes React throw the prerendered content away and render it again.
 * Once preloaded, the page component is rendered directly, without suspending.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

export function lazyWithPreload(factory: () => Promise<{ default: AnyComponent }>) {
  let Loaded: AnyComponent | null = null;
  const Lazy = lazy(factory);
  const Component = (props: Record<string, unknown>) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />);
  Component.preload = () =>
    factory().then((m) => {
      Loaded = m.default;
    });
  return Component;
}

let prerenderData: Record<string, unknown> | null = null;

/**
 * Backend GET responses the prerender saved into the page (<script id="__PRERENDER_DATA__">), keyed by
 * path + query (e.g. "/api/v1/terms/privacyList"). Lets a page's first render show the same content as
 * the snapshot instead of an empty screen; the page still refreshes it from the API afterwards.
 */
export function readPrerenderData<T = unknown>(apiPath: string): T | undefined {
  if (prerenderData === null) {
    let parsed: Record<string, unknown> = {};
    try {
      const el = document.getElementById("__PRERENDER_DATA__");
      if (el?.textContent) parsed = JSON.parse(el.textContent);
    } catch {
      parsed = {};
    }
    prerenderData = parsed;
  }
  return prerenderData[apiPath] as T | undefined;
}

/**
 * Marks the start/end of a Suspense boundary in the prerender snapshot (turned into React's <!--$--> comments).
 * The end mark also ends the hydration render: React 18 hydrates a Suspense boundary's content in a later pass
 * than the shell around it, so the flag may only be cleared once the content inside has committed.
 */
export function PrerenderBoundaryMark({ end = false }: Readonly<{ end?: boolean }>) {
  useEffect(() => {
    if (end) finishHydratingFromPrerender();
  }, [end]);
  if (!isPrerender()) return null;
  return <template data-prerender-boundary={end ? "end" : "start"} />;
}
